import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useWindow, useWindowShell, type LocalAgentClient } from '@krp/client-runtime'
import { defaultTab, HardwareScreen, isHardwareTab, PLATFORM_WINDOWS, type HardwareTabId } from '@krp/platform-ui'

import { useHardware } from './useHardware'

/** Сколько ждать первого просмотра, прежде чем выбрать вкладку по умолчанию (п. 2.3). */
export const DEFAULT_TAB_WAIT_MS = 2_000

/**
 * Binding окна «Оборудование» (RP-KA4, EQW2): что стоит на этом компьютере и работает ли касса. Данные берёт у
 * программы-драйвера на 127.0.0.1, не у сервера. Таб — адрес (`/hardware/:tab`): выбор человека заменяет запись истории
 * (`replace`), «← назад» не меняется. Вход без сегмента один раз выбирает вкладку по умолчанию; опрос таб не переключает.
 */
export function HardwareBinding({ client }: { client?: LocalAgentClient } = {}) {
  const handle = useWindow()
  const shell = useWindowShell()
  const navigate = useNavigate()
  const location = useLocation()
  const urlTab = handle.params.tab
  const tab: HardwareTabId | null = isHardwareTab(urlTab) ? urlTab : null
  const state = useHardware({ tab, client })

  const go = useCallback(
    (next: HardwareTabId) => {
      // Состояние перехода переносится как есть: «откуда пришли» у окна остаётся прежним.
      const state: unknown = location.state
      void navigate(`${shell.pathOf(PLATFORM_WINDOWS.hardware)}/${next}`, { replace: true, state })
    },
    [location.state, navigate, shell],
  )

  // Один раз: после первого просмотра (или через 2 с) адрес без таба заменяется на первый таб с неудовлетворённой потребностью.
  const [waited, setWaited] = useState(false)
  useEffect(() => {
    if (tab !== null) return
    const timer = setTimeout(() => setWaited(true), DEFAULT_TAB_WAIT_MS)
    return () => clearTimeout(timer)
  }, [tab])
  const chosen = useRef(false)
  useEffect(() => {
    if (tab !== null) {
      chosen.current = true
      return
    }
    if (chosen.current || !(state.observed || waited)) return
    chosen.current = true
    go(defaultTab(state.snapshot))
  }, [go, state.observed, state.snapshot, tab, waited])

  return <HardwareScreen snapshot={state.snapshot} links={state.links} status={state.status} tab={tab} actions={state.actions} onSelectTab={go} />
}
